<script setup>
import { useI18n } from 'vue-i18n';

defineProps({
  totalSteps: { type: Number, default: 5 },
  currentStep: { type: Number, default: 1 },
});

const { t } = useI18n();
</script>

<template>
  <div
    class="grid h-screen w-full bg-n-surface-2 text-body-main lg:grid-cols-[minmax(0,54%)_minmax(0,46%)]"
  >
    <!-- Left: form pane -->
    <section class="relative overflow-hidden">
      <!-- Grid background with corner fade -->
      <div
        class="pointer-events-none absolute inset-0 bg-[size:96px_96px] bg-[image:linear-gradient(to_right,rgb(var(--border-weak))_1px,transparent_1px),linear-gradient(to_bottom,rgb(var(--border-weak))_1px,transparent_1px)] [mask-image:radial-gradient(ellipse_80%_80%_at_100%_0%,black_5%,transparent_50%),radial-gradient(ellipse_80%_80%_at_0%_100%,black_5%,transparent_50%)] [mask-composite:add] [-webkit-mask-composite:source-over]"
      />
      <div class="relative h-full overflow-y-auto px-5 py-10 sm:px-10">
        <div class="mx-auto w-full max-w-[37.5rem]">
          <!-- Top bar: brand + progress -->
          <div class="mb-6 flex min-h-6 items-center justify-between">
            <slot name="brand" />
            <div
              class="flex gap-1.5"
              role="progressbar"
              :aria-valuemin="1"
              :aria-valuemax="totalSteps"
              :aria-valuenow="currentStep"
              :aria-label="
                t('ONBOARDING_V2.PROGRESS_LABEL', {
                  current: currentStep,
                  total: totalSteps,
                })
              "
            >
              <span
                v-for="step in totalSteps"
                :key="step"
                class="h-1 w-5 rounded-full transition-colors duration-300"
                :class="step <= currentStep ? 'bg-n-violet-9' : 'bg-n-slate-5'"
              />
            </div>
          </div>
          <!-- Form content -->
          <slot />
        </div>
      </div>
    </section>

    <!-- Right: stage pane -->
    <section
      class="relative m-2.5 hidden overflow-hidden rounded-2xl bg-[#8b7ce8] lg:block"
    >
      <!-- Violet gradient blobs -->
      <div class="pointer-events-none absolute -inset-[20%] blur-3xl">
        <div
          class="absolute left-[5%] top-[8%] h-[55%] w-[55%] rounded-full bg-[#c3baff] motion-safe:animate-pulse motion-safe:[animation-duration:9s]"
        />
        <div
          class="absolute right-0 top-[30%] h-[60%] w-[60%] rounded-full bg-[#6c55d4] motion-safe:animate-pulse motion-safe:[animation-delay:2s] motion-safe:[animation-duration:7s]"
        />
        <div
          class="absolute bottom-0 left-[25%] h-[45%] w-[45%] rounded-full bg-[#a294f5] motion-safe:animate-pulse motion-safe:[animation-delay:4s] motion-safe:[animation-duration:11s]"
        />
      </div>
      <slot name="stage" />
    </section>
  </div>
</template>
